import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, History, Loader2 } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { strategicKpisApi } from '../../services/strategicKpisApi';
import type { ActualsResponse, KpiDefinition, KpiPersonYear, KpiSelection, KpiTargetRow, ScopeType } from '../../types/strategicKpis';
import { filterBySelection, isEmptySelection } from '../../utils/strategicKpiSelection';
import { formatKpiValue } from '../../utils/strategicKpiFormat';
import type { SkpiFamily, TargetLevel } from '../../utils/strategicKpiUrl';
import { mergeStoredTarget, targetKey } from '../../utils/strategicKpiTargetGrid';
import { TargetGrid } from './targets/TargetGrid';

interface Props {
    year: number;
    /** The BUs selected in the page header — the grid shows these persons and groups. */
    bus: string[];
    /** K3: the company / team / person filter of the page header — the grid shows the matching persons and their groups. */
    selection: KpiSelection;
    /** The dashboard's actuals of the same year (for "Σ persons" next to a group target), or null. */
    actuals: ActualsResponse | null;
    canEdit: boolean;
    /** Called after every stored change so the dashboard recomputes past its 5-minute cache. */
    onChanged: () => void;
    firstYear: number;
    /** Level of the grid (URL `level`, §4). Controlled when `onLevelChange` is given; otherwise the tab keeps its own state. */
    level?: TargetLevel;
    onLevelChange?: (level: TargetLevel) => void;
    /** Family of the grid (URL `fam`; null = default). Controlled when `onFamChange` is given; otherwise the tab keeps its own state. */
    fam?: SkpiFamily | null;
    onFamChange?: (family: SkpiFamily) => void;
    /**
     * Opens the rollover panel (C5 renders it) with the years to preselect: the banner of a year without targets asks
     * for (year − 1, year), the status line of a year with targets for (year, year + 1), as the old tab did.
     */
    onOpenRollover?: (sourceYear: number, targetYear: number) => void;
    /** Opens the change-log panel (C5 renders it). */
    onOpenAudit?: () => void;
    /** Called after a PUT that wrote an audit row, so the page can reload the change log. */
    onSaved?: () => void;
    /** Changes after a real rollover: targets and prior year are loaded again. */
    refreshToken?: number;
}

const DEFAULT_FAMILY: SkpiFamily = 'visits';
const DEFAULT_LEVEL: TargetLevel = 'person';

/** The prior year's targets are only asked for when that year can exist at all. */
const hasPriorYear = (year: number, firstYear: number) => year - 1 >= firstYear;

/**
 * Targets tab (masterplan skpi-redesign C3b; E5, E11): a rollover banner when the year has no targets, otherwise a
 * status line, then the one `TargetGrid` — the last block of the tab (§2 rule 5), with the grid's own toolbar
 * (level, family, search). The change log and the rollover assistant are panels the page renders; this tab only
 * calls `onOpenAudit` / `onOpenRollover`.
 *
 * Writes go through PUT targets exactly as before (one audit row per value change, none for an identical value);
 * a stored change reloads the dashboard (`onChanged`). The Total-visits What's New marker is handled by the grid.
 *
 * Year switch (Review Phase C, C3c): the targets map has no year in its keys, so the tab tracks the year it holds
 * (`loadedYear`). Labels follow that year, the cells stay closed until the selected year has loaded, and a PUT that
 * returns after the next year has loaded is not merged into it. Catalogue, year and prior year fail separately.
 */
export function TargetsTab({
    year, bus, selection, actuals, canEdit, onChanged, firstYear, level: levelProp, onLevelChange, fam, onFamChange,
    onOpenRollover, onOpenAudit, onSaved, refreshToken = 0,
}: Props) {
    const { toast } = useToast();
    const [defs, setDefs] = useState<KpiDefinition[] | null>(null);
    const [persons, setPersons] = useState<KpiPersonYear[]>([]);
    const [targets, setTargets] = useState<Map<string, KpiTargetRow>>(new Map());
    const [prior, setPrior] = useState<Map<string, KpiTargetRow> | null>(null);
    const [priorError, setPriorError] = useState(false);
    const [loading, setLoading] = useState(false);
    /** The year whose persons and targets the state holds; null before the first load. */
    const [loadedYear, setLoadedYear] = useState<number | null>(null);
    /** The same year for the merge after a PUT, which runs outside the render that started it. */
    const loadedYearRef = useRef<number | null>(null);
    const [catalogueError, setCatalogueError] = useState<string | null>(null);
    const [yearError, setYearError] = useState<string | null>(null);
    const [saving, setSaving] = useState<Set<string>>(new Set());
    // Fallback state for a tab without callbacks; with a callback the prop is the only source.
    const [localLevel, setLocalLevel] = useState<TargetLevel>(DEFAULT_LEVEL);
    const [localFamily, setLocalFamily] = useState<SkpiFamily>(DEFAULT_FAMILY);

    const level = onLevelChange ? (levelProp ?? DEFAULT_LEVEL) : localLevel;
    const family = onFamChange ? (fam ?? DEFAULT_FAMILY) : localFamily;
    const changeLevel = useCallback((l: TargetLevel) => { if (onLevelChange) onLevelChange(l); else setLocalLevel(l); }, [onLevelChange]);
    const changeFamily = useCallback((f: SkpiFamily) => { if (onFamChange) onFamChange(f); else setLocalFamily(f); }, [onFamChange]);

    // Catalogue once; its error is its own and survives every year switch.
    useEffect(() => {
        strategicKpisApi.definitions().then(setDefs).catch(e => setCatalogueError(e instanceof Error ? e.message : 'Failed to load the KPI catalogue'));
    }, []);

    // Persons + targets of the year, and the prior year's targets (E11). A prior year that fails to load drops the
    // dots and the small prior line and says so in the card head — the grid itself stays usable.
    useEffect(() => {
        let cancelled = false;
        let priorFailed = false;
        setLoading(true);
        setYearError(null);
        const priorReq = hasPriorYear(year, firstYear)
            ? strategicKpisApi.targets(year - 1).catch(() => { priorFailed = true; return null; })
            : Promise.resolve(null);
        Promise.all([strategicKpisApi.persons(year), strategicKpisApi.targets(year), priorReq])
            .then(([p, t, pr]) => {
                if (cancelled) return;
                setPersons(p);
                setTargets(new Map(t.map(r => [targetKey(r.KpiKey, r.ScopeType, r.ScopeKey), r])));
                setPrior(pr ? new Map(pr.map(r => [targetKey(r.KpiKey, r.ScopeType, r.ScopeKey), r])) : null);
                setPriorError(priorFailed);
                loadedYearRef.current = year;
                setLoadedYear(year);
            })
            .catch(e => { if (!cancelled) setYearError(e instanceof Error ? e.message : 'Failed to load the targets'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [year, firstYear, refreshToken]);

    const nameOf = useMemo(() => new Map(persons.map(p => [p.StaffID, p.FullName])), [persons]);
    const inBus = useMemo(() => persons.filter(p => p.BUCode !== null && bus.includes(p.BUCode)), [persons, bus]);
    const filtered = !isEmptySelection(selection);
    const inScope = useMemo(() => (filtered ? filterBySelection(inBus, bus, selection) : inBus), [inBus, bus, selection, filtered]);

    const actualsGroupValue = useCallback((scopeType: ScopeType, scopeKey: string, kpiKey: string) => {
        // The Σ belongs to the targets on screen: while the next year loads, the loaded year's.
        if (!actuals || actuals.year !== loadedYear) return null;
        const g = actuals.groups.find(x => x.scopeType === scopeType && x.scopeKey === scopeKey);
        // A node the selection cuts has a Σ over the selection only — not the group's, so not shown here.
        if (!g || g.partial) return null;
        return g.values[kpiKey]?.personsTarget ?? null;
    }, [actuals, loadedYear]);

    const save = useCallback(async (kpi: KpiDefinition, scopeType: ScopeType, scopeKey: string, value: number | null): Promise<boolean> => {
        const k = targetKey(kpi.KpiKey, scopeType, scopeKey);
        setSaving(s => new Set(s).add(k));
        try {
            const r = await strategicKpisApi.putTarget(kpi.KpiKey, year, scopeType, scopeKey, value);
            // The stored value and the server's calculated targets of this scope (C after a Total / A / B write) —
            // merged only while the map still holds the year this PUT wrote (the keys carry no year).
            const now = new Date().toISOString();
            setTargets(prev => mergeStoredTarget(prev, loadedYearRef.current, {
                year, kpiKey: kpi.KpiKey, scopeType, scopeKey, newValue: r.newValue, derived: r.derived,
            }, now));
            const label = scopeType === 'PERSON' || scopeType === 'TEAM' ? (nameOf.get(scopeKey) ?? scopeKey) : scopeKey;
            const fmt = (v: number | null) => v === null ? 'none' : formatKpiValue(kpi.Unit, v);
            if (r.action === 'unchanged') toast(`${kpi.Name} · ${label}: unchanged (${fmt(r.newValue)})`, 'info');
            else toast(`${kpi.Name} · ${label}: ${fmt(r.oldValue)} → ${fmt(r.newValue)} (${r.action}, audited)`, 'success');
            if (r.audited) { onSaved?.(); onChanged(); }
            return true;
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Saving the target failed', 'error');
            return false;
        } finally {
            setSaving(s => { const next = new Set(s); next.delete(k); return next; });
        }
    }, [year, nameOf, toast, onChanged, onSaved]);

    const storedCount = useMemo(() => [...targets.values()].filter(r => !r.Derived).length, [targets]);
    const priorYear = year - 1;
    /** Labels follow the data on screen: while the next year loads they still name the year whose values are shown. */
    const shownYear = loadedYear ?? year;
    /** Cells stay closed until the selected year is on screen (TargetCell skips the PUT for an unchanged value). */
    const gridLoading = loading || loadedYear !== year;

    const error = catalogueError ?? yearError;
    if (error) return <div role="alert" className="banner error"><p>{error}</p></div>;
    if (!defs) return <div className="empty" role="status"><Loader2 className="i animate-spin" aria-hidden="true" /> Loading the catalogue…</div>;

    // E5: a year without targets starts with the rollover; once targets exist the tab shows only a status line.
    const showRollover = !gridLoading && storedCount === 0 && hasPriorYear(year, firstYear);
    const statusText = filtered
        ? `${inScope.length} of ${inBus.length} persons in ${bus.join(', ')} (filtered)`
        : `${inScope.length} persons in ${bus.join(', ')}`;

    return (
        <div className="flex flex-col gap-4" data-testid="skpi-targets-tab">
            {showRollover ? (
                <div className="banner info" role="note" data-testid="skpi-targets-rollover-banner">
                    <span className="ic"><Copy className="i" aria-hidden="true" /></span>
                    <p><b>No targets for {year} yet.</b> Start from the {priorYear} targets: copy them into {year} in one step, check the preview, then correct what changes below.</p>
                    {canEdit && onOpenRollover && (
                        <div className="actions">
                            <button type="button" className="btn primary" onClick={() => onOpenRollover(priorYear, year)} data-testid="skpi-open-rollover">Roll over {priorYear} → {year}</button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="toolbar" data-testid="skpi-targets-status" aria-busy={gridLoading || undefined}>
                    <span className="count">{statusText} · {storedCount} stored targets in {shownYear} (all BUs)</span>
                    {gridLoading && <Loader2 className="i sm animate-spin" aria-label="Loading" />}
                    <span className="tb-grow" />
                    {canEdit && onOpenRollover && storedCount > 0 && (
                        <button
                            type="button"
                            className="btn secondary sm"
                            onClick={() => onOpenRollover(year, year + 1)}
                            disabled={gridLoading}
                            data-testid="skpi-open-rollover"
                        >
                            <Copy className="i sm" aria-hidden="true" /> Roll over into {shownYear + 1}
                        </button>
                    )}
                </div>
            )}

            <TargetGrid
                year={shownYear}
                level={level}
                onLevelChange={changeLevel}
                family={family}
                onFamilyChange={changeFamily}
                defs={defs}
                persons={inScope}
                allPersons={inBus}
                bus={bus}
                nameOf={nameOf}
                targets={targets}
                prior={prior}
                priorError={priorError}
                canEdit={canEdit}
                saving={saving}
                onSave={save}
                personsSum={actualsGroupValue}
                loading={gridLoading}
                headEnd={onOpenAudit ? (
                    <button type="button" className="btn secondary sm" onClick={onOpenAudit} data-testid="skpi-open-audit">
                        <History className="i sm" aria-hidden="true" /> Change log
                    </button>
                ) : undefined}
            />
        </div>
    );
}
